/**
 * Procedure RPC wire codec — unified `POST /api/query`, `POST /api/mutation`.
 * Consumed by `@gencow/react` hooks and by `@gencow/server` via `@gencow/client/procedure-rpc`.
 * Uses `objectHasOwn` instead of `Object.hasOwn` for ES2020 `tsc` target compatibility.
 */

type RpcSegment = string | number;
type RpcMetaItem = readonly [type: number, ...path: RpcSegment[]];
type RpcSerializedPayload = { json: unknown; meta?: RpcMetaItem[] };
type RpcSerializedWithBlobs = [
  json: unknown,
  meta: RpcMetaItem[],
  maps: RpcSegment[][],
  blobs: Blob[],
];

export const MAX_RPC_BLOBS = 16;
const MAX_RPC_META_ITEMS = 2048;
const MAX_RPC_META_PATH_DEPTH = 64;
const PROCEDURE_RPC_DATA_FIELD = "data";

const RPC_BUILT_IN_TYPES = {
  BIGINT: 0,
  DATE: 1,
  NAN: 2,
  UNDEFINED: 3,
  URL: 4,
  REGEXP: 5,
  SET: 6,
  MAP: 7,
} as const;

function objectHasOwn(o: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(o, key);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isRpcSerializedPayload(value: unknown): value is RpcSerializedPayload {
  if (!isRecord(value)) return false;
  if (!objectHasOwn(value, "json")) return false;
  if (!objectHasOwn(value, "meta")) return true;
  return value.meta === undefined || Array.isArray(value.meta);
}

function formDataToRecord(form: FormData): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  for (const [key, value] of form.entries()) {
    if (!objectHasOwn(record, key)) {
      record[key] = value;
      continue;
    }
    const existing = record[key];
    record[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
  }
  return record;
}

export class ProcedureRpcDecodeError extends Error {
  status = 400 as const;
  code = "RPC_DECODE_ERROR" as const;

  constructor(message: string) {
    super(message);
    this.name = "ProcedureRpcDecodeError";
  }
}

class ProcedureRpcJsonSerializer {
  serialize(
    data: unknown,
    path: RpcSegment[] = [],
    meta: RpcMetaItem[] = [],
    maps: RpcSegment[][] = [],
    blobs: Blob[] = [],
  ): RpcSerializedWithBlobs {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      return this.serialize(formDataToRecord(data), path, meta, maps, blobs);
    }
    if (data instanceof Blob) {
      maps.push(path);
      blobs.push(data);
      // Placeholder in JSON tree — hydrated from indexed multipart parts via `maps`.
      return [null, meta, maps, blobs];
    }
    if (typeof data === "bigint") {
      meta.push([RPC_BUILT_IN_TYPES.BIGINT, ...path]);
      return [data.toString(), meta, maps, blobs];
    }
    if (data instanceof Date) {
      meta.push([RPC_BUILT_IN_TYPES.DATE, ...path]);
      return [Number.isNaN(data.getTime()) ? null : data.toISOString(), meta, maps, blobs];
    }
    if (Number.isNaN(data)) {
      meta.push([RPC_BUILT_IN_TYPES.NAN, ...path]);
      return [null, meta, maps, blobs];
    }
    if (data instanceof URL) {
      meta.push([RPC_BUILT_IN_TYPES.URL, ...path]);
      return [data.toString(), meta, maps, blobs];
    }
    if (data instanceof RegExp) {
      meta.push([RPC_BUILT_IN_TYPES.REGEXP, ...path]);
      return [data.toString(), meta, maps, blobs];
    }
    if (data instanceof Set) {
      const result = this.serialize(Array.from(data), path, meta, maps, blobs);
      meta.push([RPC_BUILT_IN_TYPES.SET, ...path]);
      return result;
    }
    if (data instanceof Map) {
      const result = this.serialize(Array.from(data.entries()), path, meta, maps, blobs);
      meta.push([RPC_BUILT_IN_TYPES.MAP, ...path]);
      return result;
    }
    if (Array.isArray(data)) {
      const json = data.map((item, index) => {
        if (item === undefined) {
          meta.push([RPC_BUILT_IN_TYPES.UNDEFINED, ...path, index]);
          return null;
        }
        return this.serialize(item, [...path, index], meta, maps, blobs)[0];
      });
      return [json, meta, maps, blobs];
    }
    if (isRecord(data)) {
      const json: Record<string, unknown> = {};
      for (const key in data) {
        if (key === "toJSON" && typeof data[key] === "function") {
          continue;
        }
        json[key] = this.serialize(data[key], [...path, key], meta, maps, blobs)[0];
      }
      return [json, meta, maps, blobs];
    }
    return [data, meta, maps, blobs];
  }

  deserialize(json: unknown, meta: readonly RpcMetaItem[]): unknown;
  deserialize(
    json: unknown,
    meta: readonly RpcMetaItem[],
    maps: readonly RpcSegment[][],
    getBlob: (index: number) => Blob,
  ): unknown;

  deserialize(
    json: unknown,
    meta: readonly RpcMetaItem[],
    maps?: readonly RpcSegment[][],
    getBlob?: (index: number) => Blob,
  ): unknown {
    if (meta.length > MAX_RPC_META_ITEMS) {
      throw new ProcedureRpcDecodeError(
        `RPC payload meta exceeds maximum item count (${MAX_RPC_META_ITEMS}).`,
      );
    }
    const ref = { data: json as any };

    if (maps && getBlob) {
      if (maps.length > MAX_RPC_BLOBS) {
        throw new ProcedureRpcDecodeError(
          `RPC payload exceeds maximum blob count (${MAX_RPC_BLOBS}).`,
        );
      }
      maps.forEach((segments, index) => {
        let currentRef: any = ref;
        let prevSegment: string | number = "data";
        for (const segment of segments) {
          currentRef = currentRef[prevSegment];
          prevSegment = segment;
          if (!objectHasOwn(currentRef, prevSegment)) {
            throw new ProcedureRpcDecodeError(
              `Invalid RPC payload path during blob deserialization. Path segment: ${String(prevSegment)}`,
            );
          }
        }
        currentRef[prevSegment] = getBlob(index);
      });
    }

    for (const item of meta) {
      if (item.length - 1 > MAX_RPC_META_PATH_DEPTH) {
        throw new ProcedureRpcDecodeError(
          `RPC payload meta path exceeds maximum depth (${MAX_RPC_META_PATH_DEPTH}).`,
        );
      }
      const type = item[0];
      let currentRef: any = ref;
      let prevSegment: string | number = "data";
      for (let index = 1; index < item.length; index++) {
        currentRef = currentRef[prevSegment];
        prevSegment = item[index]!;
        if (!objectHasOwn(currentRef, prevSegment)) {
          throw new ProcedureRpcDecodeError(
            `Invalid RPC payload path during deserialization. Path segment: ${String(prevSegment)}`,
          );
        }
      }
      switch (type) {
        case RPC_BUILT_IN_TYPES.BIGINT:
          currentRef[prevSegment] = BigInt(currentRef[prevSegment]);
          break;
        case RPC_BUILT_IN_TYPES.DATE:
          currentRef[prevSegment] = new Date(currentRef[prevSegment] ?? "Invalid Date");
          break;
        case RPC_BUILT_IN_TYPES.NAN:
          currentRef[prevSegment] = Number.NaN;
          break;
        case RPC_BUILT_IN_TYPES.UNDEFINED:
          currentRef[prevSegment] = undefined;
          break;
        case RPC_BUILT_IN_TYPES.URL:
          currentRef[prevSegment] = new URL(currentRef[prevSegment]);
          break;
        case RPC_BUILT_IN_TYPES.REGEXP: {
          const rawValue = currentRef[prevSegment];
          if (typeof rawValue !== "string") {
            throw new ProcedureRpcDecodeError("Invalid RPC payload for RegExp deserialization.");
          }
          const match = rawValue.match(/^\/(.*)\/([a-z]*)$/);
          if (!match) {
            throw new ProcedureRpcDecodeError("Invalid RPC payload for RegExp deserialization.");
          }
          const [, pattern, flags] = match;
          currentRef[prevSegment] = new RegExp(pattern!, flags);
          break;
        }
        case RPC_BUILT_IN_TYPES.SET:
          currentRef[prevSegment] = new Set(currentRef[prevSegment]);
          break;
        case RPC_BUILT_IN_TYPES.MAP:
          currentRef[prevSegment] = new Map(currentRef[prevSegment]);
          break;
      }
    }
    return ref.data;
  }
}

export interface ProcedureRpcLinkCodecOptions {}

export type ProcedureRpcRequestBody = RpcSerializedPayload | FormData;

export class ProcedureRpcLinkCodec {
  private readonly serializer = new ProcedureRpcJsonSerializer();

  constructor(_options: ProcedureRpcLinkCodecOptions = {}) {}

  /** Deserialize wire JSON (`{ json, meta }` or plain JSON) to a JavaScript value. */
  decodeWirePayload(body: unknown): unknown {
    if (body instanceof FormData) {
      return this.#deserializeFormData(body);
    }
    if (isRpcSerializedPayload(body)) {
      return this.serializer.deserialize(body.json, body.meta ?? []);
    }
    return body;
  }

  /** Serialize a value to procedure RPC wire shape (`{ json, meta? }`). */
  encodeWirePayload(value: unknown): RpcSerializedPayload {
    const [json, meta, _maps, _blobs] = this.serializer.serialize(value);
    return { json, meta: meta.length > 0 ? meta : undefined };
  }

  /**
   * Encode a procedure call for `POST /api/mutation` (procedure multipart when blobs present).
   * Returns JSON `{ json, meta? }` when no blobs are present, otherwise `multipart/form-data`.
   */
  encodeProcedureCall(payload: { name: unknown; args: unknown }): ProcedureRpcRequestBody {
    const [json, meta, maps, blobs] = this.serializer.serialize(payload);
    if (blobs.length === 0) {
      return { json, meta: meta.length > 0 ? meta : undefined };
    }
    if (blobs.length > MAX_RPC_BLOBS) {
      throw new ProcedureRpcDecodeError(
        `RPC payload exceeds maximum blob count (${MAX_RPC_BLOBS}).`,
      );
    }
    const form = new FormData();
    form.set(
      PROCEDURE_RPC_DATA_FIELD,
      JSON.stringify({
        json,
        meta: meta.length > 0 ? meta : undefined,
        maps,
      }),
    );
    blobs.forEach((blob, index) => {
      form.set(String(index), blob);
    });
    return form;
  }

  /**
   * Parse an incoming procedure call body (JSON, wire-wrapped JSON, or multipart FormData).
   */
  decodeProcedureRpcBody(body: unknown): { name: unknown; args: unknown } {
    const decoded = this.decodeWirePayload(body);
    if (!isRecord(decoded)) return { name: undefined, args: undefined };
    return { name: decoded.name, args: decoded.args };
  }

  /** Decode `multipart/form-data` procedure RPC envelope (`data` + indexed blob parts). */
  decodeProcedureMultipart(form: FormData): { name: unknown; args: unknown } {
    return this.decodeProcedureRpcBody(form);
  }

  #deserializeFormData(form: FormData): unknown {
    const dataField = form.get(PROCEDURE_RPC_DATA_FIELD);
    if (dataField === null) {
      if (form.has("_mutation")) {
        throw new ProcedureRpcDecodeError(
          'Legacy multipart layout is not supported for procedure RPC. Use the "data" field envelope.',
        );
      }
      throw new ProcedureRpcDecodeError('Missing required multipart field "data".');
    }
    if (typeof dataField !== "string") {
      throw new ProcedureRpcDecodeError('Multipart field "data" must be a JSON string.');
    }

    let parsed: { json?: unknown; meta?: RpcMetaItem[]; maps?: RpcSegment[][] };
    try {
      parsed = JSON.parse(dataField) as { json?: unknown; meta?: RpcMetaItem[]; maps?: RpcSegment[][] };
    } catch {
      throw new ProcedureRpcDecodeError('Invalid JSON in multipart field "data".');
    }

    const maps = parsed.maps ?? [];
    if (!Array.isArray(maps)) {
      throw new ProcedureRpcDecodeError('Multipart field "data" must include a "maps" array.');
    }

    const allowedKeys = new Set<string>([PROCEDURE_RPC_DATA_FIELD]);
    for (let index = 0; index < maps.length; index++) {
      allowedKeys.add(String(index));
    }

    for (const key of form.keys()) {
      if (!allowedKeys.has(key)) {
        throw new ProcedureRpcDecodeError(`Unexpected multipart field "${key}".`);
      }
    }

    for (let index = 0; index < maps.length; index++) {
      const part = form.get(String(index));
      if (part === null || !(part instanceof Blob)) {
        throw new ProcedureRpcDecodeError(`Missing blob part "${index}".`);
      }
    }

    return this.serializer.deserialize(
      parsed.json,
      parsed.meta ?? [],
      maps,
      (index) => form.get(String(index)) as Blob,
    );
  }
}
